const GOOGLE_HOME_CAROUSEL_IDS = new Set(["tasks", "calendar"]);
const GOOGLE_BRIEF_SECTION_IDS = new Set(["todos", "my_day"]);
const EDITORIAL_FRAMING_SECTION_ID = "editorial_framing";

/**
 * The public master switch is deliberately strict. Missing, stale, malformed,
 * and still-loading configuration must all keep Google account integrations
 * unavailable.
 */
export function hasGoogleIntegrations(config) {
  return config?.googleIntegrationsEnabled === true;
}

export function filterGoogleIntegrationCarouselIds(ids, enabled) {
  const values = Array.isArray(ids) ? ids : [];
  return enabled ? [...values] : values.filter((id) => !GOOGLE_HOME_CAROUSEL_IDS.has(id));
}

export function filterGoogleIntegrationBriefSectionIds(ids, enabled) {
  const values = Array.isArray(ids) ? ids : [];
  return enabled ? [...values] : values.filter((id) => !GOOGLE_BRIEF_SECTION_IDS.has(id));
}

/**
 * View-only projection for Home's card customizer. Callers retain the original
 * settings for mutations so an OFF rollout never deletes or rewrites a saved
 * Tasks/Calendar preference.
 */
export function projectGoogleIntegrationCarouselSettings(settings, enabled) {
  if (!settings || typeof settings !== "object") return settings;
  const order = filterGoogleIntegrationCarouselIds(settings.order, enabled);
  const visible = new Set(order);
  return {
    ...settings,
    order,
    hidden: Object.fromEntries(
      Object.entries(settings.hidden || {}).filter(([id]) => visible.has(id)),
    ),
  };
}

export function briefSessionContainsGoogleIntegrationSections(session) {
  return Boolean(
    session &&
    Array.isArray(session.sections) &&
    session.sections.some((section) => GOOGLE_BRIEF_SECTION_IDS.has(section?.id)),
  );
}

/**
 * Presents an existing persisted Journey without Google-backed screens while
 * retaining an exact source-index map into the immutable stored deck. The
 * persisted session is never changed by this projection.
 */
export function projectGoogleIntegrationBriefSession(session, enabled) {
  if (!session || !Array.isArray(session.sections) || session.sections.length === 0) return null;
  const sourceIndexByPresentedIndex = session.sections.flatMap((section, sourceIndex) =>
    enabled || !GOOGLE_BRIEF_SECTION_IDS.has(section?.id) ? [sourceIndex] : [],
  );
  if (sourceIndexByPresentedIndex.length === 0) return null;

  const sourceCurrentIndex = Math.max(
    0,
    Math.min(Number(session.currentIndex) || 0, session.sections.length - 1),
  );
  const firstAtOrAfter = sourceIndexByPresentedIndex.findIndex(
    (sourceIndex) => sourceIndex >= sourceCurrentIndex,
  );
  const currentIndex =
    firstAtOrAfter >= 0 ? firstAtOrAfter : Math.max(0, sourceIndexByPresentedIndex.length - 1);
  const sections = sourceIndexByPresentedIndex.map((sourceIndex) => session.sections[sourceIndex]);
  const completedCount = sourceIndexByPresentedIndex.filter(
    (sourceIndex) =>
      sourceIndex <= sourceCurrentIndex &&
      session.sections[sourceIndex]?.id !== EDITORIAL_FRAMING_SECTION_ID,
  ).length;
  const currentSection = sections[currentIndex];
  const narrationSectionVisible = sourceIndexByPresentedIndex.some(
    (sourceIndex) => session.sections[sourceIndex]?.id === session.narration?.sectionId,
  );
  const presented =
    sourceIndexByPresentedIndex.length === session.sections.length
      ? session
      : {
          ...session,
          currentIndex,
          completedCount,
          sections,
          narration: narrationSectionVisible
            ? { ...session.narration }
            : {
                ...session.narration,
                state: "idle",
                sectionId: currentSection?.id || null,
                title: currentSection?.title || null,
                positionMs: null,
                durationMs: null,
              },
        };
  return { persisted: session, presented, sourceIndexByPresentedIndex };
}

/** Maps a presented Journey page back to the corresponding persisted page. */
export function translateGoogleIntegrationBriefProgress(projection, presentedIndex) {
  const persisted = projection?.persisted;
  const sourceIndices = projection?.sourceIndexByPresentedIndex;
  if (!persisted || !Array.isArray(persisted.sections) || !Array.isArray(sourceIndices))
    return null;
  const normalizedPresentedIndex = Math.max(
    0,
    Math.min(Number(presentedIndex) || 0, sourceIndices.length - 1),
  );
  const currentIndex = sourceIndices[normalizedPresentedIndex];
  if (
    !Number.isInteger(currentIndex) ||
    currentIndex < 0 ||
    currentIndex >= persisted.sections.length
  ) {
    return null;
  }
  return {
    currentIndex,
    completedCount: persisted.sections
      .slice(0, currentIndex + 1)
      .filter((section) => section?.id !== EDITORIAL_FRAMING_SECTION_ID).length,
  };
}

/**
 * Rehydrates refreshed presented snapshots into their original source slots.
 * Hidden Google snapshots retain byte-for-byte identity and order.
 */
export function mergeGoogleIntegrationBriefSessionSections(projection, presentedSections) {
  const persisted = projection?.persisted;
  const sourceIndices = projection?.sourceIndexByPresentedIndex;
  if (
    !persisted ||
    !Array.isArray(persisted.sections) ||
    !Array.isArray(sourceIndices) ||
    !Array.isArray(presentedSections) ||
    presentedSections.length !== sourceIndices.length
  ) {
    return null;
  }
  const refreshedBySourceIndex = new Map();
  for (let presentedIndex = 0; presentedIndex < sourceIndices.length; presentedIndex += 1) {
    const sourceIndex = sourceIndices[presentedIndex];
    const persistedSection = persisted.sections[sourceIndex];
    const presentedSection = presentedSections[presentedIndex];
    if (
      !Number.isInteger(sourceIndex) ||
      sourceIndex < 0 ||
      sourceIndex >= persisted.sections.length ||
      !persistedSection ||
      presentedSection?.id !== persistedSection.id
    ) {
      return null;
    }
    refreshedBySourceIndex.set(sourceIndex, presentedSection);
  }
  return persisted.sections.map(
    (section, sourceIndex) => refreshedBySourceIndex.get(sourceIndex) || section,
  );
}
